import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import {
  FetchItemCacheKey,
  FetchItemGqlQuery,
  ID,
  NanoID,
  UUID
} from '../../../../../types';
import { ShowQueryOpts, ShowQueryErrorType } from './useShowQuery.types';

import { useShowQueryUpdateItemCache } from './hooks/useShowQueryUpdateItemCache';

import { fetchItem } from '../../actions/fetchItem';

import { LocalForage } from '../../../../../utils/LocalForage';
import { parseRequestError } from '../../../../../utils/parseRequestError';

interface ShowQueryOptions<ResponseType> {
  cacheKey: FetchItemCacheKey;
  itemKey: string;
  options: ShowQueryOpts<ResponseType>;
  placeholderData?: ResponseType;
  query: FetchItemGqlQuery;
  uuid: UUID | NanoID | ID;
}

function useShowQuery<ResponseType, ItemType>({
  cacheKey,
  itemKey,
  query,
  uuid,
  placeholderData,
  options = {}
}: ShowQueryOptions<ResponseType>) {
  const localForageCacheKey = `${cacheKey}-${uuid}`;

  const {
    data: localForagePlaceholderData,
    isFetched: placeholderDataFetched
  } = useQuery<ResponseType | null>(
    `${localForageCacheKey}-placeholder`,
    () => LocalForage.getItem<ResponseType>(localForageCacheKey),
    {
      enabled: options.enabledPlaceholder,
      onSuccess: (data) => options.onSuccess?.(data)
    }
  );

  const {
    data,
    isFetched,
    isLoading,
    isFetching,
    error,
    isPlaceholderData,
    refetch
  } = useQuery<ResponseType, ShowQueryErrorType>(
    [cacheKey, uuid],
    () =>
      fetchItem({
        query,
        uuid
      }),
    {
      cacheTime: options.cacheTime,
      staleTime: options.staleTime,
      retry: options.retry,
      enabled: options.enabled || placeholderDataFetched,
      placeholderData:
        placeholderData || localForagePlaceholderData || undefined,
      onSettled: options.onSettled,
      onSuccess: (data) => {
        options.onSuccess?.(data);
        return LocalForage.setItem<ResponseType>(localForageCacheKey, data);
      },
      onError: options.onError
    }
  );

  const updateItemCache = useShowQueryUpdateItemCache<ResponseType, ItemType>({
    fullCacheKey: [cacheKey, uuid],
    itemKey
  });

  const item: ItemType | null = data?.[itemKey] || null;

  const queryClient = useQueryClient();

  const refetchItemWithParams = useCallback(
    (cKey: FetchItemCacheKey = cacheKey, id: UUID | NanoID | ID = uuid) =>
      queryClient.fetchQuery<ResponseType>([cKey, id], () =>
        fetchItem({ query, uuid: id })
      ),
    [cacheKey, uuid, queryClient, query]
  );

  return {
    item,
    itemErrorData: error,
    itemError: parseRequestError(error),
    itemFetched: isFetched,
    itemFetching: isFetching,
    itemLoading: isLoading,
    itemIsPlaceholderData: isPlaceholderData,
    refetchItem: refetch,
    refetchItemWithParams,
    updateItemCache
  };
}

export default useShowQuery;
